/**
 * DE A DIEZ FILAS.
 *
 * QUÉ PROBLEMA RESUELVE. Un club con ochenta socios dibujaba ochenta filas, y
 * la pantalla del teléfono muestra seis: para llegar al final había que
 * arrastrar catorce pantallas. Peor todavía, cada fila trae foto, y ochenta
 * fotos son ochenta descargas antes de que se pueda leer la primera.
 *
 * DIEZ Y NO VEINTE NI CINCUENTA: diez filas de gente —foto, apodo, nombre
 * debajo— son unas dos pantallas de teléfono. Es lo que se puede recorrer sin
 * perder de vista dónde empezó.
 *
 * LA PÁGINA SE ACOMODA SOLA CUANDO LA LISTA SE ACHICA. Si alguien está en la
 * página 3 y se aprueban las solicitudes que faltaban, no queda mirando una
 * página vacía: el número se recorta al leerlo y no con un efecto, así que no
 * hay un dibujado intermedio en blanco.
 *
 * `reiniciarCon` ES PARA LAS BÚSQUEDAS. Filtrar cambia la lista entera, y
 * quedarse en la página 2 de OTRA lista es el defecto que hace pensar que la
 * búsqueda no encontró nada. Se le pasa lo que identifica la consulta —el
 * texto tecleado, los filtros— y al cambiar vuelve a la primera.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Cuántas filas por página en toda la aplicación. Una sola vez, acá. */
export const POR_PAGINA = 10;

export interface Paginado<T> {
  /** La página que se está mirando, empezando en 1. */
  pagina: number;
  /** Cuántas hay. Nunca menos de 1: una lista vacía es «página 1 de 1». */
  paginas: number;
  /** Las filas de esta página. */
  visibles: T[];
  irA: (pagina: number) => void;
}

export function usePaginado<T>(
  filas: readonly T[],
  reiniciarCon?: unknown,
  porPagina: number = POR_PAGINA,
): Paginado<T> {
  const [pedida, setPedida] = useState(1);

  useEffect(() => {
    setPedida(1);
  }, [reiniciarCon]);

  const paginas = Math.max(1, Math.ceil(filas.length / porPagina));
  // SE RECORTA AL LEER Y NO EN UN EFECTO: un efecto dibujaría primero la
  // página vacía y después la corregida, y ese parpadeo se ve.
  const pagina = Math.min(Math.max(1, pedida), paginas);
  const desde = (pagina - 1) * porPagina;

  return {
    pagina,
    paginas,
    visibles: filas.slice(desde, desde + porPagina),
    irA: (n) => setPedida(Math.min(Math.max(1, n), paginas)),
  };
}

/**
 * Los dos botones y en qué página va.
 *
 * NO SE DIBUJA CON UNA SOLA PÁGINA. Un «Página 1 de 1» con dos botones
 * apagados debajo de cuatro filas es ruido que ocupa el lugar de una fila.
 *
 * DOS BOTONES Y NO UNA FILA DE NÚMEROS. Con números hay que acertarle a un
 * «7» de doce píxeles con el pulgar, y en una lista de gente ordenada por
 * nombre nadie sabe qué hay en la página 7 antes de abrirla. Anterior y
 * siguiente son dos blancos grandes.
 *
 * `role="status"` en el texto del medio: al cambiar de página, quien usa
 * lector de pantalla oye «Página 2 de 5». Sin eso, apretar «Siguiente» no
 * anuncia nada y parece que no pasó nada.
 *
 * `de` DICE DE QUÉ LISTA ES. Una pantalla puede tener varios: la ficha de un
 * torneo tiene uno por cada categoría en borrador, más el de los inscritos
 * con los botones de revisar. Todos se llamaban «Páginas», así que quien usa
 * lector de pantalla oía el mismo nombre cuatro veces y no podía saber cuál
 * estaba tocando. Se le pasa el título de la lista —ya traducido— y el nombre
 * pasa a ser «Páginas de Inscritos».
 */
export function Paginador({
  pagina,
  paginas,
  irA,
  de,
}: {
  pagina: number;
  paginas: number;
  irA: (pagina: number) => void;
  /** De qué lista son estas páginas, ya traducido. */
  de?: string;
}): JSX.Element | null {
  const { t } = useTranslation();
  if (paginas <= 1) return null;

  return (
    <nav
      className="paginador"
      aria-label={de === undefined ? t('common.pages') : t('common.pagesOf', { what: de })}
    >
      <button
        type="button"
        className="link"
        disabled={pagina <= 1}
        onClick={() => irA(pagina - 1)}
      >
        {t('common.previousPage')}
      </button>
      <span className="paginador-donde" role="status">
        {t('common.pageOf', { page: pagina, pages: paginas })}
      </span>
      <button
        type="button"
        className="link"
        disabled={pagina >= paginas}
        onClick={() => irA(pagina + 1)}
      >
        {t('common.nextPage')}
      </button>
    </nav>
  );
}
